import { assign } from '@atds-legacy/lang';
import * as _dom from '@atds-legacy/dom';
import { MouseButtonEvent } from '@/shared/enums';
import DomEvent from './dom-event';

let isDragged = false;
const captureClick = function (target) {
  let timeout;

  const handler = (e) => {
    e.preventDefault();
    e.stopImmediatePropagation();
    clear();
  };

  const clear = () => {
    target.removeEventListener('click', handler, true);
    target.removeEventListener('auxclick', handler, true);

    if (timeout) {
      clearTimeout(timeout);
      timeout = undefined;
    }
  };

  target.addEventListener('click', handler, true);
  target.addEventListener('auxclick', handler, true);
  timeout = setTimeout(clear, 200);
};

export default function (element, options = {}) {
  const settings = assign({
    dragElement: undefined,
    acceptPropagatedEvent: true,
    preventDefault: true,
    captureClick: true,
    threshold: 10,
    axis: 'both',
  }, options);

  if (!settings.dragElement) {
    settings.dragElement = _dom.isWindow(element)
      ? element.document.body
      : element.querySelector(':first-child');
  }

  const state = {};

  const mouseDownHandler = (event) => {
    const isAllowedButton = [MouseButtonEvent.LEFT, MouseButtonEvent.MIDDLE].includes(event.button);
    const isDirectTarget = event.target === settings.dragElement;

    if (!isAllowedButton || (!settings.acceptPropagatedEvent && !isDirectTarget)) {
      return false;
    }

    settings.dragElement.classList.add('drag-scroll');
    settings.dragElement.style.cursor = 'move';

    isDragged = false;

    // Initial coordinates will be the last when dragging
    state.lastCoord = { left: event.clientX, top: event.clientY };
    state.originalCoord = { left: event.clientX, top: event.clientY };

    document.addEventListener('mouseup', mouseUpHandler);
    document.addEventListener('mousemove', mouseMoveHandler);

    if (settings.preventDefault) {
      event.preventDefault();
      return false;
    }
  };

  const mouseUpHandler = (event) => {
    document.removeEventListener('mouseup', mouseUpHandler);
    document.removeEventListener('mousemove', mouseMoveHandler);
    settings.dragElement.style.cursor = '';
    settings.dragElement.classList.remove('drag-scroll');

    if (isDragged && settings.captureClick) {
      captureClick(event.currentTarget);
    }

    if (isDragged) {
      const e = document.createEvent('CustomEvent');
      e.initCustomEvent('drag-scroll.end', true, true, { settings, state });
      element.dispatchEvent(e);
    }

    isDragged = false;

    if (settings.preventDefault) {
      event.preventDefault();
      return false;
    }
  };

  const mouseMoveHandler = (event) => {
    const delta = {
      left: (event.clientX - state.lastCoord.left),
      top: (event.clientY - state.lastCoord.top),
    };
    let deltaOriginal;

    if (delta.left || delta.top) {
      deltaOriginal = {
        left: (event.clientX - state.originalCoord.left),
        top: (event.clientY - state.originalCoord.top),
      };

      let xPos = _dom.scrollLeft(element);
      let yPos = _dom.scrollTop(element);

      // Set the scroll position relative to what ever the scroll is now
      if (settings.axis === 'x' || settings.axis === 'both') {
        xPos -= delta.left;
      }

      if (settings.axis === 'y' || settings.axis === 'both') {
        yPos -= delta.top;
      }

      _dom.scrollLeft(element, xPos);
      _dom.scrollTop(element, yPos);

      state.lastCoord = { left: event.clientX, top: event.clientY };

      if (Math.abs(deltaOriginal.left) > settings.threshold || Math.abs(deltaOriginal.top) > settings.threshold) {
        isDragged = true;

        const e = document.createEvent('CustomEvent');
        e.initCustomEvent('drag-scroll', true, true, { settings, state });
        element.dispatchEvent(e);
        this.emit(DomEvent.SYNTHETIC_MOVE);
      }
    }

    if (settings.preventDefault) {
      event.preventDefault();
      return false;
    }
  };

  settings.dragElement.addEventListener('mousedown', mouseDownHandler);
}
